import React, { useState, useEffect } from 'react';
import { Download, Search, Paperclip, X, Eye, Edit, CheckCircle, XCircle, FileText } from 'lucide-react';
import * as XLSX from 'xlsx';
import CertificadoDoc from './Certificado_Cursos_Extensión2.png';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

const getFileUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  if (path.startsWith('data:')) return path;

  // Normalizar separadores primero
  let normalizedPath = path.replace(/\\/g, '/');

  // Si la ruta contiene 'uploads', extraer desde ahí
  const uploadsMatch = normalizedPath.match(/\/uploads\/(.+)$|uploads\/(.+)$/i);
  if (uploadsMatch) {
    normalizedPath = uploadsMatch[0];
  }

  const baseUrl = API_URL.replace('/api/v1', '');

  // Asegurar que comience con / si no es una URL completa
  if (!normalizedPath.startsWith('/') && !normalizedPath.startsWith('http')) {
    normalizedPath = '/' + normalizedPath;
  }

  return `${baseUrl}${normalizedPath}`;
};

export default function KardexTalleres({ tab = 'inscripciones', setCurrentPage }) {
  const [cursos, setCursos] = useState([]);
  const [inscritos, setInscritos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isGlobalOpen, setIsGlobalOpen] = useState(false); // Por seguridad, iniciar como cerrado

  const [inscritosMap, setInscritosMap] = useState({});

  // Para modal de documentación, edición y detalles
  const [showDocsModal, setShowDocsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showCursoModal, setShowCursoModal] = useState(false);
  const [selectedInscrito, setSelectedInscrito] = useState(null);
  const [selectedCurso, setSelectedCurso] = useState(null);
  const [modalFiles, setModalFiles] = useState({}); // Para el modal de documentación

  const initialFormData = {
    nombres: '', primerApellido: '', segundoApellido: '',
    cedulaIdentidad: '', extension: '', complemento: '', genero: '',
    fechaNacimiento: '', edad: '', lugarNacimiento: '', lugarNacimientoOtro: '',
    direccion: '', telefono: '', correoElectronico: '',
    contactoNombre: '', contactoParentesco: '', contactoTelefono: '',
    numeroCuotas: '1',
    fotoCedula: null, fotoComprobanteCurso: null, fotoCedulaTutor: null
  };

  const [formData, setFormData] = useState(initialFormData);

  useEffect(() => {
    if (tab === 'inscripciones') {
      loadCursos();
      loadInscritos();
    } else if (tab === 'inscritos') {
      loadAllInscritos();
    }
  }, [tab]);


  useEffect(() => {
    if (formData.fechaNacimiento) {
      const birthDate = new Date(formData.fechaNacimiento);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const m = today.getMonth() - birthDate.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
      setFormData(prev => ({ ...prev, edad: age.toString() }));
    }
  }, [formData.fechaNacimiento]);

  const loadCursos = async () => {
    try {
      setLoading(true);
      // Verificar estado global de inscripciones (ID 4)
      const globalResponse = await fetch(`${API_URL}/direccion/parametrizaciones/verificar/4`).catch(() => null);
      if (globalResponse && globalResponse.ok) {
        const globalData = await globalResponse.json();
        setIsGlobalOpen(globalData.habilitado);
      }
      const response = await fetch(`${API_URL}/kardex/cursos`);
      const data = await response.json();
      setCursos(data.cursos || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadInscritos = async () => {
    try {
      const response = await fetch(`${API_URL}/kardex/inscritos`);
      const data = await response.json();
      const map = {};
      (data.inscritos || []).forEach(i => {
        if (!map[i.id_curso]) map[i.id_curso] = [];
        map[i.id_curso].push(i);
      });
      setInscritosMap(map);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const loadAllInscritos = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/kardex/inscritos`);
      const data = await response.json();
      setInscritos(data.inscritos || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Validar longitud de complemento
    if (name === 'complemento' && value.length > 2) {
      return;
    }

    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (fieldName, e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({ ...prev, [fieldName]: file }));
    }
  };

  const abrirInscripcion = async (curso) => {
    // Verificación dinámica antes de redirigir
    const globalRes = await fetch(`${API_URL}/direccion/parametrizaciones/verificar/4`).catch(() => null);
    if (globalRes && globalRes.ok) {
      const data = await globalRes.json();
      if (!data.habilitado) {
        setIsGlobalOpen(false);
        alert('El periodo de inscripciones general para Cursos de Extensión ha sido cerrado por la Dirección Académica. No es posible realizar nuevas inscripciones en este momento.');
        return;
      }
    } else {
      // Si no se puede verificar, por seguridad no permitir la acción
      alert('No se pudo verificar el estado del periodo de inscripciones. Intente nuevamente.');
      return;
    }

    if (isDeadlinePassed(curso)) {
      alert('El plazo para este curso ha vencido.');
      return;
    }

    // Redirigir a la página completa de inscripción pasando datos
    if (setCurrentPage) {
      setCurrentPage('registro-cursos-extension', {
        id: curso.id_curso,
        nombre: curso.nombre,
        cuotas: curso.numero_cuotas || 1,
        costo: curso.costo || curso.costo_curso || 0,
        returnTo: 'dashboard-kardex' // Para que el botón volver sepa a donde ir
      });
    } else {
      alert('Error de navegación: No se pudo redirigir al formulario de inscripción');
    }
  };


  // Dentro de KardexTalleres.jsx

  const descargarCertificado = (inscrito, modulosDelCurso) => {
    // Si no vienen módulos, intentamos parsear si el inscrito tiene el JSON string
    let modulos = modulosDelCurso;
    if (typeof modulos === 'string') {
      try { modulos = JSON.parse(modulos); } catch (e) { modulos = []; }
    }

    // Lógica idéntica de fecha (de DashboardCursosExtension.jsx)
    const obtenerUltimaFecha = (listaModulos) => {
      if (!listaModulos || !Array.isArray(listaModulos) || listaModulos.length === 0) return null;

      const timestamps = listaModulos
        .map(m => new Date(m.fecha_fin).getTime())
        .filter(ts => !isNaN(ts));

      return timestamps.length > 0 ? new Date(Math.max(...timestamps)) : null;
    };

    const ultimaFecha = obtenerUltimaFecha(modulos);

    // Log para depuración: si sale "la fecha establecida", aquí verás en consola si los módulos llegan vacíos
    console.log("Módulos recibidos:", modulos);

    const fechaTexto = ultimaFecha
      ? ultimaFecha.toLocaleDateString('es-BO', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      })
      : 'la fecha establecida';

    // 4. Datos visuales
    const nombreCompleto = `${inscrito.nombres || ''} ${inscrito.apellidos || ''}`;
    const toTitleCase = (str) =>
      str.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

    const nombreParticipante = toTitleCase(nombreCompleto);
    const fechaHoy = new Date().toLocaleDateString('es-BO', { month: 'long', year: 'numeric' });
    const fechaFormateada = `El Alto, ${fechaHoy}`;

    // 5. Ventana de impresión (Estilo idéntico a DashboardCursosExtension)
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
        <!DOCTYPE html>
        <html lang="es">
        <head>
            <meta charset="UTF-8">
            <title>Certificado - ${inscrito.curso_nombre}</title>
            <link href="https://fonts.googleapis.com/css2?family=Great+Vibes&family=Montserrat:wght@700;900&family=Open+Sans:ital,wght@0,400;1,400&display=swap" rel="stylesheet">
            <style>
                @page { size: landscape; margin: 0; }
                * { margin: 0; padding: 0; box-sizing: border-box; }
                body { font-family: 'Times New Roman', Times, serif; background-color: white; display: flex; justify-content: center; align-items: center; height: 100vh; }
                
                .certificate-container { width: 297mm; height: 210mm; position: relative; display: flex; flex-direction: column; align-items: center; padding: 28mm 30mm 10mm 30mm; }
                .header { text-align: center; margin-bottom: 30px; }
                .titulo {  sans-serif; font-size: 42pt; font-weight: 900; color: #1a2e6e; letter-spacing: 3px; text-transform: uppercase; line-height: 1; }
                .subtitulo { font-size: 20pt; font-weight: 700; color: #1a2e6e; letter-spacing: 5px; text-transform: uppercase; display: flex; align-items: center; gap: 8px; }
                .subtitulo::before, .subtitulo::after { content: ''; display: inline-block; width: 45px; height: 2px; background-color: #1a2e6e; }
                .se-otorga { font-family: 'Open Sans', sans-serif; font-style: italic; font-size: 12pt; color: #333; align-self: flex-start; margin-top: 8mm; }
                .nombre { font-family: 'Great Vibes', cursive; font-size: 44pt; color: #1a2e6e; border-bottom: 1.5px solid #1a2e6e; padding-bottom: 1mm; margin-top: 1mm; text-align: center; min-width: 140mm; }
                .details { font-size: 18px; line-height: 1.6; max-width: 80%; margin: 10px 0 0 20px; align-self: flex-start; }
                .fecha { font-size: 11pt; font-weight: 700; align-self: flex-start; margin-top: 4mm; }
                .footer { display: flex; justify-content: space-around; width: 100%; margin-top: auto; padding-bottom: 10mm; }
                .signature-box { width: 250px; text-align: center; }
                .signature-line { border-top: 2px solid #333; padding-top: 5px; font-weight: bold; font-size: 14px; text-transform: uppercase; }
                .bicentenario { position: absolute; bottom: 5mm; left: 50%; transform: translateX(-50%); font-family: 'Montserrat', sans-serif; font-weight: 700; font-size: 9pt; color: #1a2e6e; }
                .btn-print { position: fixed; bottom: 20px; right: 20px; padding: 12px 28px; background: #1a2e6e; color: white; border: none; border-radius: 8px; font-weight: bold; cursor: pointer; z-index: 9999; }
                @media print { .btn-print { display: none; } }
            </style>
        </head>
        <body>
            <button class="btn-print" onclick="window.print()">Descargar como PDF</button>
            <div class="certificate-container">
                <div class="header">
                    <div class="titulo">CERTIFICADO</div>
                    <div class="subtitulo">DE PARTICIPACIÓN</div>
                </div>
                <div class="se-otorga">Se otorga a:</div>
                <div class="nombre">${nombreParticipante}</div>
                <div class="details">
                    Por haber participado en el:<br>
                    <strong>CURSO DE EXTENSIÓN DE ${inscrito.curso_nombre.toUpperCase()}</strong><br>
                    Desarrollado en las instalaciones de la Escuela Boliviana Intercultural de Danza en el periodo de ${fechaTexto}.
                </div>
                <div class="fecha">${fechaFormateada}</div>
                <div class="footer">
                    <div class="signature-box"><div class="signature-line">Facilitador</div></div>
                    <div class="signature-box"><div class="signature-line">Jefatura de Carrera</div></div>
                </div>
                <div class="bicentenario">"2025 AÑO DEL BICENTENARIO"</div>
            </div>
        </body>
        </html>
    `);
    printWindow.document.close();
  };

  const estaInscrito = (cursoId) => {
    // return inscritosMap[cursoId] && inscritosMap[cursoId].length > 0;
    return false; // Allow multiple enrollments
  };

  const exportarExcel = () => {
    const data = filteredInscritos.map((i, idx) => ({
      'No': idx + 1,
      'Tipo': 'Inscripción',
      'Fecha Registro': i.fecha_inscripcion ? new Date(i.fecha_inscripcion).toLocaleDateString('es-BO') + ' ' + new Date(i.fecha_inscripcion).toLocaleTimeString('es-BO', { hour: '2-digit', minute: '2-digit' }) : 'S/F',
      'Nombres': i.nombres || '',
      'Apellidos': i.apellidos || '',
      'Cedula de Identidad': i.cedula || '',
      'Exp.': i.extension || '',
      'Comp.': i.complemento || '',
      'Lugar de Nacimiento': i.lugar_nacimiento || '',
      'Género': i.genero || '',
      'Fecha de Nacimiento': i.fecha_nacimiento ? new Date(i.fecha_nacimiento).toLocaleDateString('es-BO') : '',
      'Edad': i.edad || '',
      'Correo Electronico': i.email || '',
      'Telefono': i.telefono || '',
      'Direccion': i.direccion || '',
      'Nombre Curso': i.curso_nombre || '',
      'Costo Curso': i.costo_curso || i.costo || '',
      'Cuotas': i.numero_cuotas || '1',
      'Estado': i.certificado_aprobado === true ? 'Aprobado' : i.certificado_aprobado === false ? 'Rechazado' : 'Pendiente',
      'Contacto Nombre': i.contacto_nombre || '',
      'Contacto Parentesco': i.contacto_parentesco || '',
      'Contacto Telefono': i.contacto_telefono || ''
    }));

    const ws = XLSX.utils.json_to_sheet(data);

    // Auto-ajustar ancho de columnas
    const colWidths = Object.keys(data[0] || {}).map(key => ({
      wch: Math.max(key.length, ...data.map(row => String(row[key] || '').length)) + 2
    }));
    ws['!cols'] = colWidths;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Inscritos_Talleres');
    XLSX.writeFile(wb, `Reporte_Completo_Talleres_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const isDeadlinePassed = (curso) => {
    if (!curso) return false;
    const deadline = curso.fecha_limite_inscripcion || curso.fecha_inicio;
    if (!deadline) return false;
    const deadlineDate = new Date(deadline);
    // Establecer al final del día para permitir inscripciones todo el día de la fecha límite
    deadlineDate.setHours(23, 59, 59, 999);
    return new Date() > deadlineDate;
  };

  // Ver detalles completos del inscrito
  const verDetalles = (inscrito) => {
    setSelectedInscrito(inscrito);
    setShowDetailsModal(true);
  };

  // Ver documentos
  const verDocumentos = (inscrito) => {
    setSelectedInscrito(inscrito);
    setShowDocsModal(true);
  };

  // Editar inscrito
  const editarInscrito = (inscrito) => {
    setSelectedInscrito(inscrito);
    setFormData({
      ...initialFormData,
      nombres: inscrito.nombres || '',
      primerApellido: inscrito.apellidos?.split(' ')[0] || '',
      segundoApellido: inscrito.apellidos?.split(' ')[1] || '',
      cedulaIdentidad: inscrito.cedula || '',
      extension: inscrito.extension || '',
      complemento: inscrito.complemento || '',
      genero: inscrito.genero || '',
      fechaNacimiento: inscrito.fecha_nacimiento?.split('T')[0] || '',
      lugarNacimiento: inscrito.lugar_nacimiento || '',
      direccion: inscrito.direccion || '',
      telefono: inscrito.telefono || '',
      correoElectronico: inscrito.email || '',
      contactoNombre: inscrito.contacto_nombre || '',
      contactoParentesco: inscrito.contacto_parentesco || '',
      contactoTelefono: inscrito.contacto_telefono || '',
      numeroCuotas: inscrito.numero_cuotas?.toString() || '1'
    });
    setShowEditModal(true);
  };

  const handleUpdateInscrito = async () => {
    if (!selectedInscrito) return;

    try {
      const lugarFinal = formData.lugarNacimiento === 'Otro' ? formData.lugarNacimientoOtro : formData.lugarNacimiento;

      const response = await fetch(`${API_URL}/kardex/inscritos/${selectedInscrito.id_inscripcion}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tipo_usuario: selectedInscrito.tipo_usuario,
          nombres: formData.nombres,
          apellidos: `${formData.primerApellido} ${formData.segundoApellido}`.trim(),
          cedula: formData.cedulaIdentidad,
          extension: formData.extension,
          complemento: formData.complemento,
          genero: formData.genero,
          fecha_nacimiento: formData.fechaNacimiento || null,
          lugar_nacimiento: lugarFinal,
          direccion: formData.direccion,
          telefono: formData.telefono,
          email: formData.correoElectronico,
          contacto_nombre: formData.contactoNombre,
          contacto_parentesco: formData.contactoParentesco,
          contacto_telefono: formData.contactoTelefono,
          numero_cuotas: parseInt(formData.numeroCuotas)
        })
      });

      if (response.ok) {
        alert('Inscripción actualizada exitosamente');
        setShowEditModal(false);
        loadAllInscritos();
      } else {
        const data = await response.json();
        alert(data.message || 'Error al actualizar');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error al actualizar');
    }
  };

  const handleModalFileChange = (campo, e) => {
    const file = e.target.files[0];
    if (file) {
      setModalFiles(prev => ({ ...prev, [campo]: file }));
    }
  };

  const handleSaveDocs = async () => {
    if (!selectedInscrito) return;

    try {
      setLoading(true);
      const formDataUpload = new FormData();
      formDataUpload.append('tipo_usuario', selectedInscrito.tipo_usuario || 'externo');
      formDataUpload.append('cedula_identidad', selectedInscrito.cedula || 'desconocido');

      Object.keys(modalFiles).forEach(key => {
        formDataUpload.append(key, modalFiles[key]);
      });

      const response = await fetch(`${API_URL}/kardex/inscritos/${selectedInscrito.id_inscripcion}`, {
        method: 'PUT',
        body: formDataUpload
        // No Content-Type header here, browser sets it for FormData
      });

      if (response.ok) {
        alert('Documentos actualizados exitosamente');
        setModalFiles({});
        setShowDocsModal(false);
        loadAllInscritos();
        loadInscritos(); // También recargar el mapa para la pestaña de inscripciones
      } else {
        const data = await response.json();
        alert(data.message || 'Error al guardar documentos');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error de conexión al guardar');
    } finally {
      setLoading(false);
    }
  };

  // Descargar documentos del inscrito
  const descargarDocumentos = (inscrito) => {
    alert(`Descargando documentos de: ${inscrito.nombres} ${inscrito.apellidos}`);
  };

  // Ver detalles del curso (igual que Jefatura)
  const verDetallesCurso = (curso) => {
    let parsedModulos = curso.modulos;
    if (typeof parsedModulos === 'string') {
      try { parsedModulos = JSON.parse(parsedModulos); } catch (e) { parsedModulos = []; }
    }
    setSelectedCurso({ ...curso, modulos: parsedModulos });
    setShowCursoModal(true);
  };

  // Formatear días como abreviaciones (igual que Jefatura)
  const formatDias = (dias) => {
    if (!dias) return '-';
    const diasMap = {
      'lunes': 'L',
      'martes': 'M',
      'miercoles': 'Mi',
      'jueves': 'J',
      'viernes': 'V'
    };
    if (typeof dias === 'string') {
      return dias.split(',').map(d => diasMap[d.trim()] || d.trim()).join(', ');
    }
    if (Array.isArray(dias)) {
      return dias.map(d => diasMap[d] || d).join(', ');
    }
    return '-';
  };

  // Helper para color de estado
  const getEstadoColor = (estado) => {
    switch (estado) {
      case 'disponible': case 'aprobado': return 'bg-green-200 text-green-800';
      case 'pendiente_aprobacion': return 'bg-yellow-200 text-yellow-800';
      case 'rechazado': return 'bg-red-200 text-red-800';
      default: return 'bg-gray-200 text-gray-700';
    }
  };

  // Helper para label de estado
  const getEstadoLabel = (estado) => {
    switch (estado) {
      case 'disponible': case 'aprobado': return 'Aprobado';
      case 'pendiente_aprobacion': return 'Pendiente';
      case 'rechazado': return 'Rechazado';
      default: return estado || 'Pendiente';
    }
  };

  const filteredCursos = cursos.filter(c =>
    searchTerm === '' ||
    c.nombre?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredInscritos = inscritos.filter(i =>
    searchTerm === '' ||
    `${i.nombres} ${i.apellidos} ${i.cedula}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const extensiones = ['LP', 'CB', 'SC', 'OR', 'PT', 'TJ', 'CH', 'BN', 'PD'];
  const departamentos = ['La Paz', 'Cochabamba', 'Santa Cruz', 'Oruro', 'Potosí', 'Tarija', 'Chuquisaca', 'Beni', 'Pando'];

  // Documentos requeridos
  const documentosRequeridos = [
    { nombre: 'Fotografía Cédula de Identidad', campo: 'doc_cedula' },
    { nombre: 'Fotografía Comprobante Bancario Curso de Extensión', campo: 'doc_comprobante_curso' },
    { nombre: 'Fotografía Cédula de Identidad Tutor (Opcional)', campo: 'doc_cedula_tutor' }
  ];

  // ========== VISTA INSCRITOS ==========
  if (tab === 'inscritos') {
    return (
      <div key={tab} className="p-4 md:p-6 overflow-hidden">
        <div className="mb-4">
          <h1 className="text-xl font-bold text-gray-800">Kardex &gt; Talleres &gt; Inscritos</h1>
        </div>

        <div className="mb-6 bg-white p-4 rounded-xl shadow-sm border border-gray-100">
          <div className="flex flex-wrap gap-4 items-end ">
            {/* Buscar y Descargar */}
            <div className="flex-grow flex items-end gap-2">
              <div className="relative flex-grow">
                <div className="relative">
                  <Search className="absolute left-3 top-2.5 text-gray-400" size={16} />
                  <input
                    type="text"
                    placeholder="Buscar por nombre, apellido o CI..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 hover:border-blue-500"
                  />
                </div>
              </div>
              <button onClick={exportarExcel}
                className="p-2 bg-green-600 text-white rounded-full hover:bg-green-700 transition shadow-sm"
                title="Descargar Excel">
                <Download size={20} />
              </button>
            </div>

          </div>
        </div>

        {/* <div className="mb-4 flex items-center gap-3">
          <div className="flex-1 max-w-md relative">
            <input
              type="text"
              placeholder="Buscar por nombre, apellido o CI..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-full"
            />
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          </div>
          <button onClick={exportarExcel}
            className="p-2 bg-green-600 text-white rounded-full hover:bg-green-700 transition shadow-sm"
            title="Descargar Excel">
            <Download size={20} />
          </button>
        </div> */}

        {/* Tabla Única de Inscritos */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto max-w-full" style={{ maxWidth: 'calc(100vw - 280px)' }}>
            <table className="w-full text-xs whitespace-nowrap">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-2 py-3 text-center font-semibold border">No</th>
                  <th className="px-2 py-3 text-center font-semibold border">Tipo</th>
                  <th className="px-2 py-3 text-center font-semibold border">Nombres</th>
                  <th className="px-2 py-3 text-center font-semibold border">Apellidos</th>
                  <th className="px-2 py-3 text-center font-semibold border">Cédula de Identidad</th>
                  <th className="px-2 py-3 text-center font-semibold border">Extensión</th>
                  <th className="px-2 py-3 text-center font-semibold border">Correo Electrónico</th>
                  <th className="px-2 py-3 text-center font-semibold border">Nombre Curso</th>
                  <th className="px-2 py-3 text-center font-semibold border">Género</th>
                  <th className="px-2 py-3 text-center font-semibold border">Estado</th>
                  <th className="px-2 py-3 text-center font-semibold border">Certificado</th>
                  <th className="px-2 py-3 text-center font-semibold border">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="12" className="px-4 py-8 text-center text-gray-500 border">Cargando...</td></tr>
                ) : filteredInscritos.length === 0 ? (
                  <tr><td colSpan="12" className="px-4 py-8 text-center text-gray-500 border">No hay inscritos registrados</td></tr>
                ) : filteredInscritos.map((inscrito, idx) => (
                  <tr key={inscrito.id_inscripcion} className="hover:bg-gray-50">
                    <td className="px-2 py-3 text-center border">{idx + 1}</td>
                    <td className="px-2 py-3 text-center border">Inscripción</td>
                    <td className="px-2 py-3 text-center border">{inscrito.nombres || '-'}</td>
                    <td className="px-2 py-3 text-center border">{inscrito.apellidos || '-'}</td>
                    <td className="px-2 py-3 text-center border">{inscrito.cedula || '-'}</td>
                    <td className="px-2 py-3 text-center border">{inscrito.extension || '-'}</td>
                    <td className="px-2 py-3 text-center border">{inscrito.email || '-'}</td>
                    <td className="px-2 py-3 text-center border">{inscrito.curso_nombre || '-'}</td>
                    <td className="px-2 py-3 text-center border">{inscrito.genero || '-'}</td>
                    <td className="px-2 py-3 text-center border">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${inscrito.certificado_aprobado === true ? 'bg-green-100 text-green-800' :
                        inscrito.certificado_aprobado === false ? 'bg-red-100 text-red-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                        {inscrito.certificado_aprobado === true ? 'Aprobado' :
                          inscrito.certificado_aprobado === false ? 'Rechazado' : 'Pendiente'}
                      </span>
                    </td>
                    <td className="px-2 py-3 text-center border">
                      <button
                        // Asegúrate de enviar los módulos desde el objeto inscrito
                        onClick={() => inscrito.certificado_aprobado && descargarCertificado(inscrito, inscrito.modulos || [])}
                        className={`p-2 rounded-full ${inscrito.certificado_aprobado === true ? 'bg-blue-900 text-white' : 'bg-gray-300'}`}
                        disabled={inscrito.certificado_aprobado !== true}
                      >
                        <Download size={16} />
                      </button>
                    </td>
                    <td className="px-2 py-3 text-center border">
                      <div className="flex justify-center gap-1">
                        <button onClick={() => verDetalles(inscrito)}
                          className="p-1 text-blue-600 hover:bg-blue-50 rounded" title="Ver detalles completos">
                          <Eye size={16} />
                        </button>
                        <button onClick={() => verDocumentos(inscrito)}
                          className="p-1 text-green-600 hover:bg-green-50 rounded" title="Ver documentos">
                          <FileText size={16} />
                        </button>
                        <button onClick={() => editarInscrito(inscrito)}
                          className="p-1 text-gray-600 hover:bg-gray-100 rounded" title="Editar">
                          <Edit size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Modal Documentacion */}
        {showDocsModal && selectedInscrito && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl">
              <div className="px-6 py-4 border-b flex justify-between items-center">
                <h2 className="text-xl font-bold text-blue-900">Documentación</h2>
                <button onClick={() => setShowDocsModal(false)} className="text-gray-400 hover:text-gray-600"><X size={24} /></button>
              </div>
              <div className="p-6">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold border">No</th>
                      <th className="px-4 py-3 text-left font-semibold border">Documento</th>
                      <th className="px-4 py-3 text-center font-semibold border">Estado</th>
                      <th className="px-4 py-3 text-center font-semibold border">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {documentosRequeridos.map((doc, idx) => {
                      const tieneDocumento = selectedInscrito[doc.campo] || false;
                      const archivoSeleccionado = modalFiles[doc.campo];
                      return (
                        <tr key={idx} className="border-b hover:bg-gray-50">
                          <td className="px-4 py-4 border">{idx + 1}</td>
                          <td className="px-4 py-4 border">
                            <div>
                              <span className="block">{doc.nombre}</span>
                              {archivoSeleccionado && (
                                <span className="text-[10px] text-blue-600 font-medium italic">Seleccionado: {archivoSeleccionado.name}</span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-4 text-center border">
                            {tieneDocumento ? (
                              <CheckCircle className="mx-auto text-green-500" size={24} />
                            ) : (
                              <XCircle className="mx-auto text-red-500" size={24} />
                            )}
                          </td>
                          <td className="px-4 py-4 text-center border">
                            <div className="flex items-center justify-center gap-3">
                              <label className="cursor-pointer inline-block" title="Subir archivo">
                                <Paperclip size={20} className={`${archivoSeleccionado ? 'text-blue-600' : 'text-gray-400'} hover:text-blue-800 transition-colors`} />
                                <input
                                  type="file"
                                  className="hidden"
                                  accept="image/*,.pdf"
                                  onChange={(e) => handleModalFileChange(doc.campo, e)}
                                />
                              </label>

                              {tieneDocumento && (
                                <button
                                  onClick={() => window.open(getFileUrl(selectedInscrito[doc.campo]), '_blank')}
                                  className="text-green-600 hover:text-green-800 transition-colors"
                                  title="Ver/Descargar archivo existente"
                                >
                                  <Download size={20} />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div className="flex justify-end mt-6 gap-2">
                  <button onClick={() => { setShowDocsModal(false); setModalFiles({}); }}
                    className="px-6 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300">
                    Cancelar
                  </button>
                  <button onClick={handleSaveDocs}
                    disabled={loading || Object.keys(modalFiles).length === 0}
                    className={`px-6 py-2 ${loading || Object.keys(modalFiles).length === 0 ? 'bg-gray-400' : 'bg-blue-900 hover:bg-blue-800'} text-white rounded-md`}>
                    {loading ? 'Guardando...' : 'Guardar Cambios'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal Detalles */}
        {showDetailsModal && selectedInscrito && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
                <h2 className="text-xl font-bold text-blue-900">Detalles de Inscripción</h2>
                <button onClick={() => setShowDetailsModal(false)} className="text-gray-400 hover:text-gray-600"><X size={24} /></button>
              </div>
              <div className="p-6 space-y-6">
                <div>
                  <h3 className="text-lg font-semibold mb-4">Datos del Estudiante</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-600">Nombres</label>
                      <p className="text-gray-900">{selectedInscrito.nombres || '-'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-600">Apellidos</label>
                      <p className="text-gray-900">{selectedInscrito.apellidos || '-'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-600">Cédula de Identidad</label>
                      <p className="text-gray-900">{selectedInscrito.cedula || '-'} {selectedInscrito.extension || ''}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-600">Género</label>
                      <p className="text-gray-900">{selectedInscrito.genero || '-'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-600">Fecha de Nacimiento</label>
                      <p className="text-gray-900">{selectedInscrito.fecha_nacimiento ? new Date(selectedInscrito.fecha_nacimiento).toLocaleDateString() : '-'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-600">Edad</label>
                      <p className="text-gray-900">{selectedInscrito.edad || '-'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-600">Dirección</label>
                      <p className="text-gray-900">{selectedInscrito.direccion || '-'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-600">Teléfono</label>
                      <p className="text-gray-900">{selectedInscrito.telefono || '-'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-600">Correo Electrónico</label>
                      <p className="text-gray-900">{selectedInscrito.email || '-'}</p>
                    </div>
                  </div>

                  <hr className="my-4" />

                  <h3 className="font-semibold text-gray-800 mb-2">Contacto de Emergencia</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-600">Nombre Contacto</label>
                      <p className="text-gray-900">{selectedInscrito.contacto_nombre || '-'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-600">Parentesco</label>
                      <p className="text-gray-900">{selectedInscrito.contacto_parentesco || '-'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-600">Teléfono Contacto</label>
                      <p className="text-gray-900">{selectedInscrito.contacto_telefono || '-'}</p>
                    </div>
                  </div>

                  <hr className="my-4" />

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-600">Curso</label>
                      <p className="text-gray-900">{selectedInscrito.curso_nombre || '-'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-600">Número de Cuotas</label>
                      <p className="text-gray-900">{selectedInscrito.numero_cuotas || '1'}</p>
                    </div>
                  </div>

                  <div className="flex justify-end mt-6">
                    <button onClick={() => setShowDetailsModal(false)}
                      className="px-6 py-2 bg-blue-900 text-white rounded-md hover:bg-blue-800">
                      Cerrar
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal Edición */}
        {showEditModal && selectedInscrito && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
                <h2 className="text-xl font-bold text-blue-900">Editar Inscripción</h2>
                <button onClick={() => setShowEditModal(false)} className="text-gray-400 hover:text-gray-600"><X size={24} /></button>
              </div>

              <div className="p-6 space-y-6">
                <div>
                  <h3 className="text-lg font-semibold mb-4">Datos Personales</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Nombres*</label>
                      <input type="text" name="nombres" value={formData.nombres} onChange={handleInputChange}
                        placeholder="Ingrese nombres" className="w-full px-3 py-2 border rounded-md" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Primer Apellido*</label>
                      <input type="text" name="primerApellido" value={formData.primerApellido} onChange={handleInputChange}
                        placeholder="Ingrese primer apellido" className="w-full px-3 py-2 border rounded-md" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Segundo Apellido</label>
                      <input type="text" name="segundoApellido" value={formData.segundoApellido} onChange={handleInputChange}
                        placeholder="Ingrese segundo apellido" className="w-full px-3 py-2 border rounded-md" />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Cédula de Identidad*</label>
                      <input type="text" name="cedulaIdentidad" value={formData.cedulaIdentidad} onChange={handleInputChange}
                        placeholder="Ej: 12345678" className="w-full px-3 py-2 border rounded-md" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Extensión</label>
                      <select name="extension" value={formData.extension} onChange={handleInputChange}
                        className="w-full px-3 py-2 border rounded-md">
                        <option value="">Seleccionar</option>
                        {extensiones.map(e => <option key={e} value={e}>{e}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Género</label>
                      <select name="genero" value={formData.genero} onChange={handleInputChange}
                        className="w-full px-3 py-2 border rounded-md">
                        <option value="">Seleccionar</option>
                        <option value="M">Masculino</option>
                        <option value="F">Femenino</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Fecha Nacimiento</label>
                      <input type="date" name="fechaNacimiento" value={formData.fechaNacimiento} onChange={handleInputChange}
                        className="w-full px-3 py-2 border rounded-md" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Teléfono</label>
                      <input type="tel" name="telefono" value={formData.telefono} onChange={handleInputChange}
                        placeholder="Ej: 71234567" className="w-full px-3 py-2 border rounded-md" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Dirección</label>
                      <input type="text" name="direccion" value={formData.direccion} onChange={handleInputChange}
                        placeholder="Ingrese dirección" className="w-full px-3 py-2 border rounded-md" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Correo Electrónico</label>
                      <input type="email" name="correoElectronico" value={formData.correoElectronico} onChange={handleInputChange}
                        placeholder="ejemplo@correo.com" className="w-full px-3 py-2 border rounded-md" />
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-4">Pago</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Número de Cuotas</label>
                      <select name="numeroCuotas" value={formData.numeroCuotas} onChange={handleInputChange}
                        className="w-full px-3 py-2 border rounded-md">
                        <option value="1">1</option>
                        <option value="2">2</option>
                        <option value="3">3</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div className="p-4 border-t flex justify-end">
                  <button onClick={handleUpdateInscrito}
                    className="px-6 py-2 bg-blue-900 text-white rounded-md hover:bg-blue-800">
                    Guardar Cambios
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    );
  }

  // ========== VISTA INSCRIPCIONES (Cursos disponibles) ==========
  return (
    <div key={tab} className="p-4 md:p-6 overflow-hidden">
      <div className="mb-4">
        <h1 className="text-xl font-bold text-gray-800">Kardex &gt; Talleres &gt; Inscripción</h1>
      </div>

      <div className="mb-4 flex items-center gap-3">
        <div className="flex-1 max-w-md relative">
          <input
            type="text"
            placeholder="Buscar curso..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-full"
          />
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
        </div>
      </div>

      {/* Tabla de Cursos - Estructura similar a Jefatura */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-3 py-3 text-left font-semibold">No</th>
                <th className="px-3 py-3 text-left font-semibold">Nombre Curso</th>
                <th className="px-3 py-3 text-left font-semibold">Descripción</th>
                <th className="px-3 py-3 text-left font-semibold">Costo (Bs.)</th>
                <th className="px-3 py-3 text-left font-semibold">Cuotas</th>
                <th className="px-3 py-3 text-center font-semibold">Inscritos</th>
                <th className="px-3 py-3 text-center font-semibold">Cupos</th>
                <th className="px-3 py-3 text-center font-semibold">Estado</th>
                <th className="px-3 py-3 text-center font-semibold">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading ? (
                <tr><td colSpan="7" className="px-4 py-8 text-center text-gray-500">Cargando...</td></tr>
              ) : filteredCursos.length === 0 ? (
                <tr><td colSpan="7" className="px-4 py-8 text-center text-gray-500">No hay cursos disponibles</td></tr>
              ) : filteredCursos.map((curso, index) => (
                <tr key={curso.id_curso} className="hover:bg-gray-50">
                  <td className="px-3 py-3">{index + 1}</td>
                  <td className="px-3 py-3 font-medium">{curso.nombre || '-'}</td>
                  <td className="px-3 py-3 max-w-xs truncate">{curso.descripcion || '-'}</td>
                  <td className="px-3 py-3">{curso.costo ? `Bs. ${parseFloat(curso.costo).toFixed(2)}` : '-'}</td>
                  <td className="px-3 py-3">{curso.numero_cuotas || '-'}</td>
                  <td className="px-3 py-3 text-center">{curso.inscritos || 0}</td>
                  <td className="px-3 py-3 text-center font-bold">
                    <span className={curso.disponibles <= 0 ? 'text-red-600' : 'text-green-600'}>
                      {curso.disponibles ?? curso.capacidad}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-center">
                    <span className={`px-2 py-1 rounded-full text-xs ${curso.estado === 'disponible' || curso.estado === 'aprobado' ? 'bg-green-200 text-green-800' :
                      curso.estado === 'pendiente_aprobacion' ? 'bg-yellow-200 text-yellow-800' :
                        curso.estado === 'rechazado' ? 'bg-red-200 text-red-800' :
                          'bg-gray-200 text-gray-700'
                      }`}>
                      {curso.estado === 'disponible' || curso.estado === 'aprobado' ? 'Aprobado' :
                        curso.estado === 'pendiente_aprobacion' ? 'Pendiente' :
                          curso.estado === 'rechazado' ? 'Rechazado' :
                            curso.estado || 'Pendiente'}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-center">
                    <div className="flex justify-center gap-2">
                      <button
                        onClick={() => verDetallesCurso(curso)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-full"
                        title="Ver detalles del curso"
                      >
                        <Eye size={18} />
                      </button>
                      <button
                        onClick={() => abrirInscripcion(curso)}
                        className={`px-4 py-2 rounded-full text-xs font-medium ${curso.disponibles <= 0 || isDeadlinePassed(curso) || !isGlobalOpen ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-blue-900 text-white hover:bg-blue-800'}`}
                        disabled={curso.disponibles <= 0 || isDeadlinePassed(curso) || !isGlobalOpen}
                      >
                        {curso.disponibles <= 0 ? 'Lleno' : isDeadlinePassed(curso) ? 'Cerrado' : !isGlobalOpen ? 'Cerrado Dirección' : 'Inscribir'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal Ver Detalles del Curso - Igual que Jefatura */}
      {showCursoModal && selectedCurso && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-800">Detalles del Curso</h2>
              <button onClick={() => { setShowCursoModal(false); setSelectedCurso(null); }} className="text-gray-400 hover:text-gray-600">
                <X size={24} />
              </button>
            </div>
            <div className="p-6 space-y-6">
              {/* Información General del Curso */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-blue-800 mb-4">Información del Curso</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <span className="text-sm text-gray-500">Nombre Curso</span>
                    <p className="font-medium">{selectedCurso.nombre || '-'}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Descripción</span>
                    <p className="font-medium">{selectedCurso.descripcion || '-'}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Precio Curso</span>
                    <p className="font-medium">Bs. {selectedCurso.costo || '-'}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Número de Cuotas</span>
                    <p className="font-medium">{selectedCurso.numero_cuotas || '-'}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Capacidad Total</span>
                    <p className="font-medium">{selectedCurso.capacidad || '-'}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Inscritos</span>
                    <p className="font-medium">{selectedCurso.inscritos || 0}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Cupos Disponibles</span>
                    <p className={`font-bold ${selectedCurso.disponibles <= 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {selectedCurso.disponibles ?? selectedCurso.capacidad}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Estado</span>
                    <br />
                    <span className={`inline-block px-2 py-1 rounded-full text-xs ${getEstadoColor(selectedCurso.estado)}`}>
                      {getEstadoLabel(selectedCurso.estado)}
                    </span>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Fecha Límite Inscripción</span>
                    <p className={`font-bold ${isDeadlinePassed(selectedCurso) ? 'text-red-600' : 'text-blue-600'}`}>
                      {selectedCurso.fecha_limite_inscripcion ? new Date(selectedCurso.fecha_limite_inscripcion).toLocaleDateString() :
                        selectedCurso.fecha_inicio ? new Date(selectedCurso.fecha_inicio).toLocaleDateString() : 'No definida'}
                      {isDeadlinePassed(selectedCurso) && ' (Plazo Vencido)'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Módulos del Curso */}
              <div>
                <h3 className="text-lg font-semibold text-blue-800 mb-4">Módulos del Curso</h3>
                <div className="bg-white rounded-lg shadow overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-100 border-b">
                        <tr>
                          <th className="px-3 py-3 text-left font-semibold">Módulo</th>
                          <th className="px-3 py-3 text-left font-semibold">Facilitador</th>
                          <th className="px-3 py-3 text-left font-semibold">Fecha Inicio</th>
                          <th className="px-3 py-3 text-left font-semibold">Fecha Fin</th>
                          <th className="px-3 py-3 text-left font-semibold">Hora Inicio</th>
                          <th className="px-3 py-3 text-left font-semibold">Hora Fin</th>
                          <th className="px-3 py-3 text-center font-semibold">Días</th>
                          <th className="px-3 py-3 text-left font-semibold">Duración</th>
                          <th className="px-3 py-3 text-left font-semibold">Carga Horaria</th>
                          <th className="px-3 py-3 text-left font-semibold">Sesiones</th>
                          <th className="px-3 py-3 text-left font-semibold">Sala</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {selectedCurso.modulos && selectedCurso.modulos.length > 0 ? (
                          selectedCurso.modulos.map((modulo, idx) => (
                            <tr key={idx} className="hover:bg-gray-50">
                              <td className="px-3 py-3 font-medium">{modulo.nombre || `Módulo ${idx + 1}`}</td>
                              <td className="px-3 py-3">{modulo.facilitador || '-'}</td>
                              <td className="px-3 py-3">{modulo.fecha_inicio ? new Date(modulo.fecha_inicio).toLocaleDateString() : '-'}</td>
                              <td className="px-3 py-3">{modulo.fecha_fin ? new Date(modulo.fecha_fin).toLocaleDateString() : '-'}</td>
                              <td className="px-3 py-3">{modulo.hora_inicio || '-'}</td>
                              <td className="px-3 py-3">{modulo.hora_fin || '-'}</td>
                              <td className="px-3 py-3 text-center">{formatDias(modulo.dias)}</td>
                              <td className="px-3 py-3">{modulo.duracion || '-'}</td>
                              <td className="px-3 py-3">{modulo.carga_horaria || '-'}</td>
                              <td className="px-3 py-3">{modulo.sesiones || '-'}</td>
                              <td className="px-3 py-3">{modulo.sala || '-'}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="11" className="px-4 py-8 text-center text-gray-500">
                              No hay módulos registrados para este curso
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Botón Cerrar */}
              <div className="flex justify-end pt-4">
                <button
                  onClick={() => { setShowCursoModal(false); setSelectedCurso(null); }}
                  className="px-6 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}